import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  ClipboardCheck, 
  FileText, 
  ShieldCheck, 
  CheckCircle, 
  ArrowRight, 
  Search, 
  AlertCircle, 
  Zap, 
  Database,
  ScanLine,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AlaynaClaimScrubbingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Alayna, Claim Scrubbing Agent</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Alayna, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI Claim Scrubbing Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Alayna reviews every claim before it goes out, checking for errors, missing attachments, and coding issues. She fixes problems automatically, ensuring a 99% first-pass acceptance rate.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="relative w-full max-w-lg">
                    {/* Main Scanner Card */}
                    <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-100 relative z-10">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                             <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                     <ScanLine className="w-5 h-5 text-blue-600" />
                                 </div>
                                 <div>
                                     <div className="font-bold text-slate-900">Claim #88291</div>
                                     <div className="text-xs text-slate-500">Scanning in progress...</div>
                                 </div>
                             </div>
                             <div className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold border border-yellow-200 animate-pulse">
                                 Analyzing
                             </div>
                        </div>

                        {/* Scan Lines Animation */}
                        <div className="relative h-64 bg-slate-50 rounded-xl border border-slate-200 overflow-hidden mb-6">
                            <div className="absolute top-0 left-0 w-full h-1 bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)] animate-[scan_2s_ease-in-out_infinite]"></div>
                            
                            <div className="p-4 space-y-3 opacity-50">
                                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                                <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                                <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                                <div className="h-32 bg-slate-200 rounded w-full mt-4"></div>
                            </div>

                            {/* Alert Overlay */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-white p-4 rounded-xl shadow-lg border border-red-100 flex items-center gap-3 animate-[popIn_0.5s_ease-out_forwards]">
                                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                        <AlertTriangle className="w-4 h-4 text-red-500" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-slate-900">Missing Narrative</div>
                                        <div className="text-xs text-slate-500">D2740 requires explanation</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2">
                                <Zap className="w-4 h-4" />
                                Auto-Fix
                            </Button>
                            <Button variant="outline" className="flex-1 border-slate-200 gap-2">
                                <FileText className="w-4 h-4" />
                                View Details
                            </Button>
                        </div>
                    </div>

                    {/* Floating Stats */}
                    <div className="absolute -right-8 top-32 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">First-Pass Rate</div>
                                <div className="text-lg font-bold text-slate-900">99.2%</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Proactive Error Detection
            </h2>
            <p className="text-lg text-gray-600">
              Don't wait for a denial letter. Catch issues instantly before the claim even leaves your office.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Scanning</h3>
              <p className="text-gray-600">
                Alayna reads clinical notes and procedure codes to ensure they match. She flags discrepancies like a missing PA for a crown.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Auto-Correction</h3>
              <p className="text-gray-600">
                Simple errors like incorrect subscriber IDs or missing modifiers are fixed automatically based on historical data.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Payer-Specific Rules</h3>
              <p className="text-gray-600">
                She knows that Delta needs an X-ray for D2740 but MetLife also needs a narrative. She enforces these rules strictly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-green-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <ScanLine className="w-4 h-4" />
                The Scrubbing Process
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Alayna Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                It happens in milliseconds. As soon as a claim is created, Alayna is reviewing it.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Ingests Claim</h4>
                    <p className="text-slate-400 text-sm">Pulls data from the claim form and the patient's clinical chart.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Runs 500+ Checks</h4>
                    <p className="text-slate-400 text-sm">Verifies coding, eligibility, frequency limits, and documentation.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Flags Issues</h4>
                    <p className="text-slate-400 text-sm">Highlights missing items in red and suggests fixes.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Clears for Submission</h4>
                    <p className="text-slate-400 text-sm">Once clean, the claim is instantly sent to the clearinghouse.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <ClipboardCheck className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Claim Audit</div>
                              <div className="text-xs text-slate-400">Claim #88291</div>
                          </div>
                      </div>
                      <div className="bg-red-500/10 text-red-400 px-3 py-1 rounded-full text-xs font-bold border border-red-500/20">
                          Needs Attention
                      </div>
                  </div>

                  <div className="space-y-4">
                      <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                          <div className="flex items-start gap-3">
                              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                              <div>
                                  <div className="font-bold text-slate-200 text-sm">Missing Narrative</div>
                                  <p className="text-xs text-slate-400 mt-1">
                                      Procedure D2740 requires a narrative for replacement.
                                  </p>
                              </div>
                          </div>
                          <div className="mt-3 pl-8">
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs">
                                  Auto-Generate from Notes
                              </Button>
                          </div>
                      </div>

                      <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                          <div className="flex items-start gap-3">
                              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                              <div>
                                  <div className="font-bold text-slate-200 text-sm">Eligibility Verified</div>
                                  <p className="text-xs text-slate-400 mt-1">
                                      Patient has active coverage with MetLife.
                                  </p>
                              </div>
                          </div>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">99%</div>
              <div className="text-gray-600 font-medium">First-Pass Acceptance</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">14 Days</div>
              <div className="text-gray-600 font-medium">Reduction in DSO</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Compliance</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Stop denials before they happen.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Alayna scrub your claims so you get paid faster, the first time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Get Clean Claims
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlaynaClaimScrubbingPage;